import tw, { styled } from 'twin.macro';
import ResponsiveImage from '@/components/ResponsiveImage/ResponsiveImage';
import { StyledContainer as StyledStarContainer } from '@/components/StarRating/StarRating.styles';
import AnnouncementButton from '../AnnouncementButton/AnnouncementButton';
import { Container } from '../AnnouncementButton/AnnouncementButton.styles';
import InfoTag from '../InfoTag/InfoTag';
import LanguagesGridBlockV3 from '../LanguagesGridBlockV3/LanguagesGridBlockV3';

const headLineFontSize = {
  small: tw`text-heading-l`,
  medium: tw`text-heading-l lg:text-display-xl`,
  large: tw`text-heading-l lg:text-display-xxl`,
};

const bodyFontSize = {
  small: tw`text-body-l lg:text-body-m`,
  medium: tw`text-body-l`,
  large: tw`text-body-l `,
};

const contentLayout = {
  col_5_7: tw`lg:col-span-5`,
  col_6_6: tw`lg:col-span-6`,
};

const mediaLayout = {
  col_5_7: tw`lg:col-span-7`,
  col_6_6: tw`lg:col-span-6`,
};

export const StyledButtons = styled.div`
  ${tw`px-2 md:px-0`}
  ${tw`grid md:flex gap-2 justify-center md:items-center lg:justify-start`}
`;

export const StyledSection = styled.section<{
  hasVisual: boolean;
  backgroundColor: Queries.kontent_item_secondary_hero_block_elements_background_color_enum;
}>`
  ${tw`relative`}
  ${tw`px-2`}
  ${tw`text-center lg:text-left`}
  ${tw`flex`}

  ${({ hasVisual }) => (hasVisual ? tw`text-left` : tw`text-center`)}
  ${StyledButtons} {
    ${({ hasVisual }) => !hasVisual && tw`md:items-center md:justify-center`}
  }
  ${({ backgroundColor }) =>
    backgroundColor === 'white' ? tw`bg-neutrals-n1` : tw`bg-ui-colours-ui1`}

  ${StyledStarContainer} {
    ${tw`mb-4`}
    ${({ hasVisual }) => !hasVisual && tw`justify-center`}
  }
`;

export const StyledResponsiveBackgroundImage = styled(ResponsiveImage)`
  ${tw`absolute top-0 left-0 h-full w-full z-0`}

  &:before {
    ${tw`hidden`}
  }
`;

export const StyledContainer = styled.div<{
  hasVisual: boolean;
}>`
  ${tw`container`}
  ${tw`container-stack-context`}
  ${tw`lg:px-0`}

  ${tw`z-10`}
  ${tw`relative`}
  ${({ hasVisual }) =>
    hasVisual
      ? tw`grid-cols-6 gap-x-7 md:block lg:grid lg:gap-4 lg:grid-cols-12 lg:items-center`
      : tw`flex justify-center`}
`;

export const StyledContent = styled.div<{
  layout: Queries.kontent_item_hero_block_homepage_elements_layout_enum;
  size: Queries.kontent_item_hero_block_homepage_elements_size_enum;
  hasVisual: boolean;
}>`
  ${tw`md:px-3 lg:px-0`}
  ${tw`lg:mb-0`}
  ${tw`col-span-8`}

  ${({ layout }) => contentLayout[layout]}
  ${({ hasVisual }) =>
    hasVisual ? tw`mb-5 text-center lg:text-left` : tw`text-center max-w-[734px] mb-0`}
`;

export const StyledLogo = styled.div<{ hasVisual: boolean }>`
  ${tw`mb-3 md:mb-4`}

  img {
    ${tw`max-h-[36px] md:max-h-[72px] w-[300px]`}
    ${({ hasVisual }) => hasVisual && tw`mx-auto lg:ml-0`}
  }
`;

export const StyledAnnouncementButton = styled(AnnouncementButton)<{
  hasVisual: boolean;
  backgroundColor: Queries.kontent_item_secondary_hero_block_elements_background_color_enum;
}>`
  ${tw`mb-3`}

  ${Container} {
    ${({ hasVisual }) => hasVisual && tw`lg:justify-start`}
    ${({ backgroundColor }) =>
      backgroundColor === 'white' ? tw`bg-neutrals-n1` : tw`bg-ui-colours-ui1`}
  }
`;

export const StyledSubheadline = styled.p`
  ${tw`font-inter font-semi-bold`}
  ${tw`text-primary-p1 text-tag-m uppercase`}
  ${tw`mb-2`}
`;

export const StyledHeadline = styled.div<{
  size: Queries.kontent_item_secondary_hero_block_elements_size_enum;
}>`
  ${tw`py-0 lg:px-0`}
  ${tw`my-0 mb-2`}
  ${tw`font-poppins font-semi-bold`}
  ${tw`text-secondary-s1`}

  ${({ size }) => headLineFontSize[size]}
  em {
    ${tw`not-italic`}
    &:before,
    &:after {
      ${tw`text-secondary-s1`}
    }
    &:before {
      content: '{';
    }
    &:after {
      content: '}';
    }
  }
`;

export const StyledMediaHero = styled.div<{
  layout: Queries.kontent_item_hero_block_homepage_elements_layout_enum;
}>`
  & img {
    ${tw`relative`}
  }
  & picture:before {
    ${tw`pt-0`}
  }

  ${({ layout }) => mediaLayout[layout]}
`;

export const StyledRichText = styled.div<{
  hasVisual: boolean;
  size: Queries.kontent_item_hero_block_homepage_elements_size_enum;
}>`
  ${tw`mb-4`}
  ${tw`text-primary-p1`}
  ${tw`font-inter`}
  ${({ size }) => bodyFontSize[size]}
  ${StyledStarContainer} {
    ${tw`justify-center lg:justify-start`}
    ${({ hasVisual }) => !hasVisual && tw`lg:justify-center`}
  }
`;

export const StyledInfoTag = styled(InfoTag)`
  ${tw`mb-2`}
`;

export const StyledLanguagesGridBlockV3 = styled(LanguagesGridBlockV3)<{
  backgroundColor: Queries.kontent_item_secondary_hero_block_elements_background_color_enum;
}>`
  ${tw`px-0 !py-0`} // !important on py overrides vertical padding from CMS via applyPaddingCSS.

  ${({ backgroundColor }) =>
    backgroundColor === 'white' ? tw`bg-neutrals-n1` : tw`bg-ui-colours-ui1`}

  /* Target the direct container div from LanguagesGridBlockV3 */
  > div {
    ${tw`grid justify-center`}
    ${tw`gap-2 md:gap-[23px]`}
    ${tw`grid-cols-[repeat(auto-fit,58px)]`} // 58px is the size of language icon including padding
  }
`;
